// Centralized axios instance with auth interceptor
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
});

// Endpoints whose 401 should never auto-logout the user.
// These are user-action failures (wrong current password etc), not token problems.
const AUTH_SAFE_ENDPOINTS = [
  "/api/auth/login",
  "/api/auth/change-password",
  "/api/auth/admin/change-user-password",
];

// Reasons that ARE valid token problems and should trigger logout
function isTokenInvalidError(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail !== "string") return false;
  const d = detail.toLowerCase();
  return (
    d.includes("not authenticated") ||
    d.includes("invalid or expired token") ||
    d.includes("user not found") ||
    d.includes("could not validate")
  );
}

api.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem("envirolytics_token");
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (e) {
    // localStorage may be disabled (Safari private mode, iframe sandbox) — proceed without token
    if (typeof console !== "undefined") console.warn("[api] localStorage unavailable in request interceptor:", e?.message);
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    const url = err?.config?.url || "";
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    const isAuthSafe = AUTH_SAFE_ENDPOINTS.some((p) => url.includes(p));
    const isDeactivated = status === 403 && detail === "Account is deactivated";

    // Logout when the backend has invalidated the session or the account itself.
    // The deactivated-account response is intentionally 403 because the token is
    // valid cryptographically but the account is no longer permitted to use it.
    if (!isAuthSafe && (isDeactivated || (status === 401 && isTokenInvalidError(err)))) {
      try {
        const hadToken = !!localStorage.getItem("envirolytics_token");
        localStorage.removeItem("envirolytics_token");
        localStorage.removeItem("envirolytics_user");
        if (hadToken && typeof window !== "undefined") {
          // Notify the app (AuthGate listens) so it can redirect to login cleanly.
          window.dispatchEvent(new CustomEvent("envirolytics:auth-expired"));
        }
      } catch (e) {
        if (typeof console !== "undefined") console.warn("[api] localStorage unavailable during 401 cleanup:", e?.message);
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;

// Backend-served file (e.g. uploaded aeration videos) URL builder. Prepends
// REACT_APP_BACKEND_URL so <video src="..."> resolves against the API host,
// not the frontend origin.
export function backendAssetUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const base = API_BASE.replace(/\/$/, "");
  const p = pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`;
  return `${base}${p}`;
}

// Build a fully-qualified backend URL for raw fetch() / <a href> / window.open
// calls. Guarantees the URL never becomes "undefined/api/..." when
// REACT_APP_BACKEND_URL is missing at build time (e.g. Azure VM without an
// .env file) — falls back to same-origin so nginx routes /api/* to FastAPI.
export function apiUrl(path) {
  const base = (API_BASE || "").replace(/\/$/, "");
  const p = path ? (path.startsWith("/") ? path : `/${path}`) : "";
  return `${base}${p}`;
}

